import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import api from '../../services/api';
import { RaceDetail as RaceDetailType, RaceCard } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { pickHero } from './heroImages';
import ContinueRaceModal from '../Training/ContinueRaceModal';
import './RaceDetail.css';

const TERRAIN_ICONS: Record<string, string> = {
  trail: '⛰️', road: '🏙️', track: '🏟️', mixed: '🗺️',
};

function decodeHtmlEntities(text: string): string {
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

function formatDescription(raw: string): string {
  const decoded = decodeHtmlEntities(raw);
  // Normalize Windows-style line endings, collapse 3+ newlines to 2
  return decoded.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

const TIER_LABELS: Record<string, string> = {
  world_major: 'World Major',
  gold_label: 'Gold Label',
  iconic_ultra: 'Iconic Ultra',
  major: 'Major Race',
};

function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="star-picker">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          className={`star-btn ${n <= (hovered || value) ? 'active' : ''}`}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
        >★</button>
      ))}
    </div>
  );
}

function StarRow({ rating, label }: { rating?: number; label: string }) {
  if (!rating) return null;
  const stars = Math.round(rating);
  return (
    <div className="race-review-dim">
      <span className="race-review-dim-label">{label}</span>
      <span className="race-review-stars">{'★'.repeat(stars)}{'☆'.repeat(5 - stars)}</span>
      <span className="race-review-dim-score">{rating.toFixed(1)}</span>
    </div>
  );
}

function weatherDescription(maxF?: number, precipIn?: number): string {
  if (maxF === undefined) return '';
  const temp = maxF < 40 ? 'Cold' : maxF < 58 ? 'Cool' : maxF < 72 ? 'Mild' : maxF < 82 ? 'Warm' : 'Hot';
  const rain = !precipIn || precipIn < 0.05 ? 'dry' : precipIn < 0.2 ? 'light chance of rain' : 'wet';
  return `Typically ${temp.toLowerCase()} and ${rain}`;
}

const RaceDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [race, setRace] = useState<RaceDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Enrichment state
  const [reddit, setReddit] = useState<{ posts: any[]; fetched_at: string } | null>(null);
  const [redditLoading, setRedditLoading] = useState(false);
  const [redditError, setRedditError] = useState<string | null>(null);

  const [aiSummary, setAiSummary] = useState<{ summary: string; generated_at: string } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const [weather, setWeather] = useState<any | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState<string | null>(null);

  const [similar, setSimilar] = useState<RaceCard[]>([]);

  // Train for This Race
  const [trainLoading, setTrainLoading] = useState(false);
  const [trainDone, setTrainDone] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [relayLegMiles, setRelayLegMiles] = useState<string>('');
  const [existingGoalName, setExistingGoalName] = useState<string | null>(null);
  const [existingGoalDate, setExistingGoalDate] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewDistRan, setReviewDistRan] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.getRace(slug)
      .then(r => {
        setRace(r);
        if (r.events.length > 0) {
          const withDist = r.events.filter(e => e.distance_miles);
          if (withDist.length) setSelectedEventId(withDist.reduce((a, b) => (b.distance_miles! > a.distance_miles! ? b : a)).id);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    api.getSimilarRaces(slug).then(setSimilar).catch(() => {});
    if (isAuthenticated) {
      api.getRaceGoal().then(g => {
        if (g?.race_name) setExistingGoalName(g.race_name);
        if (g?.race_date) setExistingGoalDate(g.race_date);
      }).catch(() => {});
    }
  }, [slug]);

  const loadReddit = async () => {
    if (!slug || redditLoading) return;
    setRedditLoading(true);
    setRedditError(null);
    try {
      const data = await api.getRaceReddit(slug);
      setReddit(data);
    } catch {
      setRedditError('Could not load Reddit discussions.');
    } finally {
      setRedditLoading(false);
    }
  };

  const loadAiSummary = async () => {
    if (!slug || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const data = await api.getRaceAiSummary(slug);
      setAiSummary(data);
    } catch {
      setAiError('Could not generate summary.');
    } finally {
      setAiLoading(false);
    }
  };

  const loadWeather = async () => {
    if (!slug || weatherLoading) return;
    setWeatherLoading(true);
    setWeatherError(null);
    try {
      const data = await api.getRaceWeather(slug);
      setWeather(data);
    } catch {
      setWeatherError('Weather data not available for this race.');
    } finally {
      setWeatherLoading(false);
    }
  };

  const selectedEvent = race?.events.find(e => e.id === selectedEventId);
  const isRelayLeg = selectedEvent?.event_type === 'relay' && Number(relayLegMiles) > 0;
  const buildRacePayload = () => ({
    race_name: isRelayLeg ? `${race!.name} — Relay leg` : race!.name,
    race_date: race!.race_date,
    race_distance_miles: (selectedEvent?.event_type === 'relay' && Number(relayLegMiles) > 0)
      ? Number(relayLegMiles)
      : selectedEvent?.distance_miles ?? race!.events[0]?.distance_miles ?? 26.2,
    ...(race!.total_elevation_gain_ft ? { race_elevation_gain_ft: race!.total_elevation_gain_ft } : {}),
  });

  const doTrainForRace = async () => {
    if (!race) return;
    setShowConfirmModal(false);
    setTrainLoading(true);
    try {
      await api.updateRaceGoal(buildRacePayload());
      setTrainDone(true);
      setTimeout(() => navigate('/plan'), 800);
    } catch {
      setTrainLoading(false);
    }
  };

  // Continuation: roll the current race into this one without a base rebuild.
  const doContinueRace = async () => {
    if (!race) return;
    await api.continueIntoNextRace(buildRacePayload());
    setShowConfirmModal(false);
    setTrainDone(true);
    setTimeout(() => navigate('/plan'), 800);
  };

  // Continuation only applies when this race is after the current goal race.
  const canContinue = !!existingGoalDate && !!race && race.race_date > existingGoalDate;

  const handleTrainForRace = () => {
    if (!race) return;
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    if (existingGoalName) {
      setShowConfirmModal(true);
      return;
    }
    doTrainForRace();
  };

  const handleSubmitReview = async () => {
    if (!race || reviewRating === 0) return;
    setReviewSubmitting(true);
    try {
      await api.submitReview(race.id, {
        overall_rating: reviewRating,
        distance_ran: reviewDistRan || undefined,
      });
      setReviewSuccess(true);
      setShowReviewForm(false);
      // Refresh race to show new review
      const updated = await api.getRace(slug!);
      setRace(updated);
    } catch {
      // silent fail
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (loading) return (
    <div className="race-detail-page">
      <Navbar />
      <div className="race-detail-loading"><div className="race-detail-spinner" /></div>
    </div>
  );

  if (error || !race) return (
    <div className="race-detail-page">
      <Navbar />
      <div className="race-detail-error">
        <p>{error || 'Race not found.'}</p>
        <button onClick={() => navigate(-1)}>← Back to Search</button>
      </div>
    </div>
  );

  const registrationUrl = race.registration_url;
  const sourceName = race.source
    ? race.source.charAt(0).toUpperCase() + race.source.slice(1)
    : 'External Site';

  const tierLabel = race.race_tier ? TIER_LABELS[race.race_tier] : null;

  // Events with distances for the train picker
  const distanceEvents = race.events.filter(e => e.distance_miles);

  return (
    <div className="race-detail-page">
      <Navbar />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div
        className="race-detail-hero"
        style={{
          backgroundImage: `url(${race.hero_image_url || pickHero(race.id, race.terrain)})`
        }}
      >
        <div className="race-detail-hero-overlay">
          <button className="race-detail-back" onClick={() => navigate(-1)}>← Back to Search</button>
          <div className="race-detail-hero-content">
            <div className="race-detail-hero-badges">
              {tierLabel && (
                <span className={`race-tier-badge tier-${race.race_tier}`}>{tierLabel}</span>
              )}
              {race.terrain && (
                <span className="race-detail-terrain-badge">
                  {TERRAIN_ICONS[race.terrain]} {race.terrain}
                </span>
              )}
            </div>
            <h1 className="race-detail-title">{race.name}</h1>
            <div className="race-detail-hero-meta">
              <span>📅 {formatDate(race.race_date)}</span>
              {(race.city || race.state) && (
                <span>📍 {[race.city, race.state].filter(Boolean).join(', ')}</span>
              )}
              {race.total_elevation_gain_ft && (
                <span>⬆️ {race.total_elevation_gain_ft.toLocaleString()} ft gain</span>
              )}
            </div>
            {distanceEvents.length > 0 && (
              <div className="race-detail-hero-distances">
                {distanceEvents.map(e => (
                  <span key={e.id} className="race-distance-pill">
                    {e.distance_label || `${e.distance_miles?.toFixed(0)}mi`}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Quick Stats Bar ───────────────────────────────────────────── */}
      <div className="race-detail-stats-bar">
        {race.total_elevation_gain_ft && (
          <div className="race-stat-item">
            <span className="race-stat-icon">⬆️</span>
            <div>
              <div className="race-stat-value">{race.total_elevation_gain_ft.toLocaleString()} ft</div>
              <div className="race-stat-label">Elevation Gain</div>
            </div>
          </div>
        )}
        {race.altitude_ft && (
          <div className="race-stat-item">
            <span className="race-stat-icon">🏔️</span>
            <div>
              <div className="race-stat-value">{race.altitude_ft.toLocaleString()} ft</div>
              <div className="race-stat-label">Max Altitude</div>
            </div>
          </div>
        )}
        {race.terrain && (
          <div className="race-stat-item">
            <span className="race-stat-icon">{TERRAIN_ICONS[race.terrain]}</span>
            <div>
              <div className="race-stat-value" style={{ textTransform: 'capitalize' }}>{race.terrain}</div>
              <div className="race-stat-label">Course Type</div>
            </div>
          </div>
        )}
        {race.composite_rating && (
          <div className="race-stat-item">
            <span className="race-stat-icon">⭐</span>
            <div>
              <div className="race-stat-value">{race.composite_rating.toFixed(1)}</div>
              <div className="race-stat-label">{race.total_reviews} review{race.total_reviews !== 1 ? 's' : ''}</div>
            </div>
          </div>
        )}
        {distanceEvents.length > 0 && (
          <div className="race-stat-item">
            <span className="race-stat-icon">🏃</span>
            <div>
              <div className="race-stat-value">
                {distanceEvents.length === 1
                  ? (distanceEvents[0].distance_label || `${distanceEvents[0].distance_miles?.toFixed(0)}mi`)
                  : `${distanceEvents.length} distances`}
              </div>
              <div className="race-stat-label">Distance{distanceEvents.length > 1 ? 's' : ''}</div>
            </div>
          </div>
        )}
      </div>

      <div className="race-detail-container">
        <div className="race-detail-layout">

          {/* ── Main Content ─────────────────────────────────────────── */}
          <div className="race-detail-main">

            {/* About */}
            {race.description && (
              <section className="race-detail-section">
                <h2>About This Race</h2>
                <p className="race-detail-description">{formatDescription(race.description)}</p>
              </section>
            )}

            {/* Course Details */}
            {(race.course_description || race.total_elevation_gain_ft || race.altitude_ft) && (
              <section className="race-detail-section">
                <h2>Course Details</h2>
                {race.course_description && (
                  <p className="race-detail-description">{formatDescription(race.course_description)}</p>
                )}
                <div className="race-detail-course-stats">
                  {race.total_elevation_gain_ft && (
                    <div className="race-detail-course-stat">
                      <span className="race-course-stat-label">Elevation Gain</span>
                      <span className="race-course-stat-value">{race.total_elevation_gain_ft.toLocaleString()} ft</span>
                    </div>
                  )}
                  {race.altitude_ft && (
                    <div className="race-detail-course-stat">
                      <span className="race-course-stat-label">Max Altitude</span>
                      <span className="race-course-stat-value">{race.altitude_ft.toLocaleString()} ft</span>
                    </div>
                  )}
                  {race.terrain && (
                    <div className="race-detail-course-stat">
                      <span className="race-course-stat-label">Terrain</span>
                      <span className="race-course-stat-value">{TERRAIN_ICONS[race.terrain]} {race.terrain}</span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Distances */}
            {race.events.length > 0 && (
              <section className="race-detail-section">
                <h2>Distances & Pricing</h2>
                <div className="race-detail-events">
                  {race.events.map(ev => (
                    <div key={ev.id} className="race-detail-event-card">
                      <div className="race-detail-event-name">{ev.name}</div>
                      {ev.distance_miles && (
                        <div className="race-detail-event-dist">{ev.distance_miles.toFixed(1)} mi</div>
                      )}
                      {ev.price_cents && (
                        <div className="race-detail-event-price">${(ev.price_cents / 100).toFixed(0)}</div>
                      )}
                      {ev.capacity && (
                        <div className="race-detail-event-cap">
                          {ev.current_registrations || 0} / {ev.capacity} registered
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* AI Summary — only shown when the race has no provider description */}
            {!race.description && (
            <section className="race-detail-section race-enrich-section">
              <h2>🤖 About This Race <span className="enrich-badge">AI</span></h2>
              {!aiSummary && !aiLoading && (
                <div className="race-enrich-prompt">
                  <p>Get an AI-generated overview — course character, difficulty, who it's ideal for, and prep tips.</p>
                  <button className="race-enrich-btn" onClick={loadAiSummary}>
                    Tell me about this race
                  </button>
                </div>
              )}
              {aiLoading && (
                <div className="race-enrich-loading">
                  <div className="race-detail-spinner" />
                  <span>Analyzing race details…</span>
                </div>
              )}
              {aiError && <p className="race-enrich-error">{aiError}</p>}
              {aiSummary && (
                <div className="race-ai-summary">
                  <p>{aiSummary.summary}</p>
                </div>
              )}
            </section>
            )}

            {/* Weather */}
            <section className="race-detail-section race-enrich-section">
              <h2>🌤️ Typical Race Day Weather</h2>
              {!weather && !weatherLoading && (
                <div className="race-enrich-prompt">
                  <p>Historical 3-year average for {new Date(race.race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.</p>
                  <button className="race-enrich-btn" onClick={loadWeather}>
                    Check weather history
                  </button>
                </div>
              )}
              {weatherLoading && (
                <div className="race-enrich-loading">
                  <div className="race-detail-spinner" />
                  <span>Fetching historical weather…</span>
                </div>
              )}
              {weatherError && <p className="race-enrich-error">{weatherError}</p>}
              {weather && !weatherError && (
                <div className="race-weather-widget">
                  <div className="weather-stats">
                    {weather.avg_temp_max_f !== undefined && (
                      <div className="weather-stat">
                        <span className="weather-stat-icon">🌡️</span>
                        <div>
                          <div className="weather-stat-value">
                            {Math.round(weather.avg_temp_min_f ?? weather.avg_temp_max_f)}–{Math.round(weather.avg_temp_max_f)}°F
                          </div>
                          <div className="weather-stat-label">High / Low</div>
                        </div>
                      </div>
                    )}
                    {weather.avg_precip_inch !== undefined && (
                      <div className="weather-stat">
                        <span className="weather-stat-icon">🌧️</span>
                        <div>
                          <div className="weather-stat-value">{weather.avg_precip_inch.toFixed(2)}"</div>
                          <div className="weather-stat-label">Avg Precip</div>
                        </div>
                      </div>
                    )}
                    {weather.avg_wind_mph !== undefined && (
                      <div className="weather-stat">
                        <span className="weather-stat-icon">💨</span>
                        <div>
                          <div className="weather-stat-value">{Math.round(weather.avg_wind_mph)} mph</div>
                          <div className="weather-stat-label">Max Wind</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="weather-description">
                    {weatherDescription(weather.avg_temp_max_f, weather.avg_precip_inch)} · 3-year historical average
                  </p>
                </div>
              )}
            </section>

            {/* Reddit */}
            <section className="race-detail-section race-enrich-section">
              <h2>💬 What Runners Are Saying</h2>
              {!reddit && !redditLoading && (
                <div className="race-enrich-prompt">
                  <p>Real runner discussions from r/ultrarunning, r/running, and r/trailrunning.</p>
                  <button className="race-enrich-btn" onClick={loadReddit}>
                    Load discussions
                  </button>
                </div>
              )}
              {redditLoading && (
                <div className="race-enrich-loading">
                  <div className="race-detail-spinner" />
                  <span>Loading Reddit discussions…</span>
                </div>
              )}
              {redditError && <p className="race-enrich-error">{redditError}</p>}
              {reddit && (
                reddit.posts.length === 0 ? (
                  <p className="race-detail-no-reviews">No Reddit discussions found for this race yet.</p>
                ) : (
                  <div className="reddit-posts">
                    {reddit.posts.map((post, i) => (
                      <a
                        key={i}
                        className="reddit-post-card"
                        href={post.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="reddit-post-title">{post.title}</div>
                        {post.selftext && (
                          <div className="reddit-post-preview">{post.selftext}</div>
                        )}
                        <div className="reddit-post-meta">
                          <span className="reddit-subreddit">r/{post.subreddit}</span>
                          <span>▲ {post.score}</span>
                          <span>💬 {post.num_comments}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                )
              )}
            </section>

            {/* Reviews */}
            <section className="race-detail-section">
              <h2>
                Reviews
                {race.composite_rating && (
                  <span className="race-detail-rating-summary">
                    ★ {race.composite_rating.toFixed(1)} · {race.total_reviews} review{race.total_reviews !== 1 ? 's' : ''}
                  </span>
                )}
              </h2>

              {race.reviews.length === 0 && !showReviewForm ? (
                <p className="race-detail-no-reviews">No reviews yet. Be the first!</p>
              ) : (
                <div className="race-detail-reviews">
                  {race.reviews.map(r => (
                    <div key={r.id} className="race-review-card">
                      <div className="race-review-header">
                        <div>
                          <span className="race-review-stars-main">
                            {'★'.repeat(Math.round(r.overall_rating))}
                            {'☆'.repeat(5 - Math.round(r.overall_rating))}
                          </span>
                          <span className="race-review-score">{r.overall_rating.toFixed(1)}</span>
                        </div>
                        <div className="race-review-meta">
                          {r.source !== 'racefinder' && (
                            <span className="race-review-source">via {r.source}</span>
                          )}
                          {r.is_verified && <span className="race-review-verified">✓ Verified</span>}
                          {r.distance_ran && <span className="race-review-distance">{r.distance_ran}</span>}
                          {r.race_date_ran && (
                            <span className="race-review-date">{new Date(r.race_date_ran).getFullYear()}</span>
                          )}
                        </div>
                      </div>
                      <div className="race-review-dims">
                        <StarRow rating={r.course_scenery}    label="Scenery" />
                        <StarRow rating={r.organization}      label="Organization" />
                        <StarRow rating={r.aid_stations}      label="Aid Stations" />
                        <StarRow rating={r.crowd_support}     label="Crowd Support" />
                        <StarRow rating={r.medal_swag}        label="Medal/Swag" />
                        <StarRow rating={r.post_race}         label="Post-Race" />
                        <StarRow rating={r.parking_logistics} label="Parking" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Review form */}
              {isAuthenticated && !reviewSuccess && (
                <div className="race-review-form-wrap">
                  {!showReviewForm ? (
                    <button
                      className="race-write-review-btn"
                      onClick={() => setShowReviewForm(true)}
                    >
                      ✍️ Write a Review
                    </button>
                  ) : (
                    <div className="race-review-form">
                      <h3>Your Review</h3>
                      <div className="review-form-field">
                        <label>Overall Rating <span className="req">*</span></label>
                        <StarPicker value={reviewRating} onChange={setReviewRating} />
                      </div>
                      {race.events.length > 0 && (
                        <div className="review-form-field">
                          <label>Distance You Ran <span className="opt">(optional)</span></label>
                          <select
                            className="review-select"
                            value={reviewDistRan}
                            onChange={e => setReviewDistRan(e.target.value)}
                          >
                            <option value="">Select distance…</option>
                            {race.events.map(e => (
                              <option key={e.id} value={e.distance_label || e.name}>
                                {e.distance_label || e.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="review-form-actions">
                        <button
                          className="review-submit-btn"
                          disabled={reviewRating === 0 || reviewSubmitting}
                          onClick={handleSubmitReview}
                        >
                          {reviewSubmitting ? 'Submitting…' : 'Submit Review'}
                        </button>
                        <button
                          className="review-cancel-btn"
                          onClick={() => { setShowReviewForm(false); setReviewRating(0); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {reviewSuccess && (
                <p className="review-success">✓ Review submitted! Thank you.</p>
              )}
              {!isAuthenticated && (
                <p className="review-signin-note">
                  <Link to="/">Sign in</Link> to leave a review.
                </p>
              )}
            </section>

            {/* Similar Races */}
            {similar.length > 0 && (
              <section className="race-detail-section">
                <h2>Similar Races</h2>
                <div className="similar-races-grid">
                  {similar.map(r => (
                    <Link key={r.id} to={`/races/${r.slug}`} className="similar-race-card">
                      {r.hero_image_url && (
                        <div
                          className="similar-race-img"
                          style={{ backgroundImage: `url(${r.hero_image_url})` }}
                        />
                      )}
                      <div className="similar-race-info">
                        <div className="similar-race-name">{r.name}</div>
                        <div className="similar-race-meta">
                          {[r.city, r.state].filter(Boolean).join(', ')}
                          {r.distances.length > 0 && ` · ${r.distances[0]}`}
                        </div>
                        <div className="similar-race-date">
                          {new Date(r.race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* ── Sidebar ───────────────────────────────────────────────── */}
          <aside className="race-detail-sidebar">

            {/* Registration CTA */}
            <div className="race-detail-cta-card">
              <div className={`race-detail-reg-status status-${race.registration_status}`}>
                {race.registration_status === 'open' ? 'Registration Open' :
                 race.registration_status === 'sold_out' ? 'Sold Out' :
                 race.registration_status === 'upcoming' ? 'Opens Soon' :
                 race.registration_status === 'closed' ? 'Registration Closed' : 'Check Site'}
              </div>

              {registrationUrl && race.registration_status !== 'closed' && (
                <a
                  className="race-detail-register-btn"
                  href={registrationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Register on {sourceName} ↗
                </a>
              )}

              {race.website_url && race.website_url !== registrationUrl && (
                <a
                  className="race-detail-website-link"
                  href={race.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Race Website ↗
                </a>
              )}
            </div>

            {/* Train for This Race */}
            <div className="race-detail-train-card">
              <div className="train-card-header">
                <span className="train-card-icon">🏋️</span>
                <div>
                  <div className="train-card-title">Train for This Race</div>
                  <div className="train-card-subtitle">Build a personalized training plan</div>
                </div>
              </div>

              {distanceEvents.length > 1 && (
                <div className="train-distance-selector">
                  <label>Select distance</label>
                  <select
                    value={selectedEventId ?? ''}
                    onChange={e => setSelectedEventId(Number(e.target.value))}
                    className="train-distance-select"
                  >
                    {distanceEvents.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.distance_label || `${e.distance_miles?.toFixed(0)}mi`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {selectedEvent?.event_type === 'relay' && (
                <div className="train-distance-selector">
                  <label>Your relay leg (miles)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.1"
                    className="train-distance-select"
                    placeholder="e.g. 13.1"
                    value={relayLegMiles}
                    onChange={e => setRelayLegMiles(e.target.value)}
                  />
                </div>
              )}

              <button
                className={`race-train-btn ${trainDone ? 'done' : ''}`}
                onClick={handleTrainForRace}
                disabled={trainLoading || trainDone}
              >
                {trainDone ? '✓ Race Set! Redirecting…' :
                 trainLoading ? 'Setting up…' :
                 isAuthenticated ? 'Build My Training Plan →' : 'Sign in to Train →'}
              </button>
            </div>

            {/* Quick info */}
            <div className="race-detail-info-card">
              <h3>Race Info</h3>
              <div className="race-detail-info-rows">
                <div className="race-detail-info-row">
                  <span>Date</span>
                  <span>{formatDate(race.race_date)}</span>
                </div>
                {race.start_time && (
                  <div className="race-detail-info-row">
                    <span>Start Time</span>
                    <span>{race.start_time}</span>
                  </div>
                )}
                {race.city && (
                  <div className="race-detail-info-row">
                    <span>Location</span>
                    <span>{[race.city, race.state].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {race.terrain && (
                  <div className="race-detail-info-row">
                    <span>Terrain</span>
                    <span>{TERRAIN_ICONS[race.terrain]} {race.terrain}</span>
                  </div>
                )}
                {race.total_elevation_gain_ft && (
                  <div className="race-detail-info-row">
                    <span>Elevation</span>
                    <span>{race.total_elevation_gain_ft.toLocaleString()} ft</span>
                  </div>
                )}
                {race.is_verified && (
                  <div className="race-detail-info-row">
                    <span>Verified</span>
                    <span>✓ Confirmed</span>
                  </div>
                )}
              </div>
            </div>
          </aside>

        </div>
      </div>

      {/* ── Confirm overwrite modal ───────────────────────────────── */}
      {showConfirmModal && existingGoalName && (
        <ContinueRaceModal
          currentRaceName={existingGoalName}
          newRaceName={race.name}
          canContinue={canContinue}
          onContinue={doContinueRace}
          onFresh={doTrainForRace}
          onClose={() => setShowConfirmModal(false)}
        />
      )}

      {/* ── Login prompt modal ───────────────────────────────────── */}
      {showLoginModal && (
        <div className="race-modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="race-modal" onClick={e => e.stopPropagation()}>
            <h3>Sign In to Train</h3>
            <p>
              Connect your Strava account to build a personalized training plan
              for <strong>{race.name}</strong>.
            </p>
            <div className="race-modal-actions">
              <Link to="/" className="race-modal-confirm">Connect with Strava</Link>
              <button className="race-modal-cancel" onClick={() => setShowLoginModal(false)}>
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaceDetail;
